import axiosInstance from "../config/axiosInstance";

/**
 * Camada de acesso ao módulo de cobrança.
 *
 * Todos os métodos devolvem já o `data` útil e propagam uma mensagem de erro
 * legível — as telas não precisam conhecer o formato de resposta do backend.
 */

const unwrap = (response) => response?.data?.data ?? response?.data;

/** Extrai a mensagem que o backend enviou, caindo num texto genérico. */
export const getErrorMessage = (error, fallback = "Algo deu errado. Tente novamente.") =>
  error?.response?.data?.message || error?.message || fallback;

/** Código de erro semântico (ex.: PLAN_LIMIT_REACHED) para reagir na UI. */
export const getErrorCode = (error) => error?.response?.data?.code || null;

export const getCatalog = async () => unwrap(await axiosInstance.get("/billing/catalog"));

export const getMyBilling = async () => unwrap(await axiosInstance.get("/billing/me"));

/**
 * Inicia o pagamento de um plano.
 * @param {object} params
 * @param {string} params.planCode      Ex.: "PREMIUM"
 * @param {string} params.method        PIX | CARD | CARD_RECURRING | BOLETO | CHECKOUT_PRO
 * @param {string} [params.token]       Token do cartão gerado pelo SDK do MP
 */
export const checkoutPlan = async ({ planCode, method, ...rest }) =>
  unwrap(await axiosInstance.post("/billing/checkout/plan", { planCode, method, ...rest }));

/**
 * Inicia o pagamento de uma oferta avulsa (destaque, banner, consultoria...).
 * `metadata` carrega os campos obrigatórios da oferta (productId, categoryId).
 */
export const checkoutOffer = async ({ offerCode, method, quantity = 1, metadata = {}, ...rest }) =>
  unwrap(
    await axiosInstance.post("/billing/checkout/offer", {
      offerCode,
      method,
      quantity,
      metadata,
      ...rest,
    })
  );

export const listOrders = async (params = {}) =>
  unwrap(await axiosInstance.get("/billing/orders", { params }));

export const getOrder = async (orderId) =>
  unwrap(await axiosInstance.get(`/billing/orders/${orderId}`));

/** Consulta o status ao vivo no gateway (usado no polling do PIX). */
export const getOrderStatus = async (orderId) =>
  unwrap(await axiosInstance.get(`/billing/orders/${orderId}/status`));

export const cancelOrder = async (orderId) =>
  unwrap(await axiosInstance.post(`/billing/orders/${orderId}/cancel`));

export const cancelSubscription = async ({ atPeriodEnd = true } = {}) =>
  unwrap(await axiosInstance.post("/billing/subscription/cancel", { atPeriodEnd }));

export const reactivateSubscription = async () =>
  unwrap(await axiosInstance.post("/billing/subscription/reactivate"));

export const listEntitlements = async () =>
  unwrap(await axiosInstance.get("/billing/entitlements"));

export const cancelEntitlement = async (entitlementId) =>
  unwrap(await axiosInstance.post(`/billing/entitlements/${entitlementId}/cancel`));

export const getInstallments = async ({ bin, amountCents, paymentMethodId }) =>
  unwrap(
    await axiosInstance.get("/billing/installments", {
      params: { bin, amountCents, paymentMethodId },
    })
  );

/* ── Administração ─────────────────────────────────────────────────────────── */

export const adminGetSummary = async () =>
  unwrap(await axiosInstance.get("/billing/admin/summary"));

export const adminListOrders = async (params = {}) =>
  unwrap(await axiosInstance.get("/billing/admin/orders", { params }));

/** Venda assistida: gera link de pagamento com valor negociado. */
export const adminCreateOrderLink = async (payload) =>
  unwrap(await axiosInstance.post("/billing/admin/orders", payload));

export const adminRefund = async (mpPaymentId, amountCents) =>
  unwrap(
    await axiosInstance.post(`/billing/admin/payments/${mpPaymentId}/refund`, { amountCents })
  );

export const adminListWebhooks = async (params = {}) =>
  unwrap(await axiosInstance.get("/billing/admin/webhooks", { params }));

export const adminRetryWebhooks = async (limit = 20) =>
  unwrap(await axiosInstance.post("/billing/admin/webhooks/retry", { limit }));

export const adminSyncPlans = async () =>
  unwrap(await axiosInstance.post("/billing/admin/plans/sync"));

/* ── Formatadores compartilhados ───────────────────────────────────────────── */

export const formatBRL = (cents) =>
  (Number(cents || 0) / 100).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });

export const formatDate = (value) =>
  value ? new Date(value).toLocaleDateString("pt-BR") : "—";

export const formatDateTime = (value) =>
  value
    ? new Date(value).toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

/** Rótulo do ciclo de cobrança para exibir junto do preço. */
export const intervalLabel = (interval) => {
  if (interval === "MONTH") return "/mês";
  if (interval === "YEAR") return "/ano";
  return "";
};

export const ORDER_STATUS_LABELS = {
  PENDING: { label: "Aguardando pagamento", tone: "warning" },
  IN_PROCESS: { label: "Em análise", tone: "info" },
  PAID: { label: "Pago", tone: "success" },
  REJECTED: { label: "Recusado", tone: "danger" },
  CANCELED: { label: "Cancelado", tone: "danger" },
  REFUNDED: { label: "Estornado", tone: "danger" },
  CHARGED_BACK: { label: "Chargeback", tone: "danger" },
  EXPIRED: { label: "Expirado", tone: "danger" },
};

export const SUBSCRIPTION_STATUS_LABELS = {
  ATIVA: { label: "Ativa", tone: "success" },
  PENDENTE: { label: "Pendente", tone: "warning" },
  INADIMPLENTE: { label: "Pagamento pendente", tone: "warning" },
  PAUSADA: { label: "Pausada", tone: "info" },
  CANCELADA: { label: "Cancelada", tone: "danger" },
  EXPIRADA: { label: "Expirada", tone: "danger" },
  SEM_ASSINATURA: { label: "Plano gratuito", tone: "info" },
};
